import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { Button } from '../../components/ui/Button';
import { PenTool, BookOpen, Users, Target } from 'lucide-react';
export function AboutPublication() {
  const editors = [{
    name: 'Sam Chen',
    role: 'Editor-in-Chief',
    bio: 'Sam is a fat writer and cultural critic whose work has appeared in The New Inquiry, Catapult, and elsewhere. They bring 10 years of editorial experience and a commitment to centering marginalized voices.'
  }, {
    name: 'Jordan Martinez',
    role: 'Managing Editor',
    bio: 'Jordan is a fat disabled poet and essayist. They oversee day-to-day operations and work closely with contributors to develop their pieces.'
  }, {
    name: 'Alex Thompson',
    role: 'Poetry Editor',
    bio: 'Alex is a fat trans poet whose work explores embodiment, desire, and resistance. They curate our poetry section and run our annual poetry prize.'
  }, {
    name: 'Riley Johnson',
    role: 'Art Director',
    bio: 'Riley is a fat visual artist and designer. They commission illustrations, photography, and visual essays that challenge dominant narratives about bodies.'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-675">
      <HeroSection title="About The Publication" subtitle="A journal of fat thought, art, and politics." tag="The Digital Publication" bgColor="orange" data-id="element-676" />

      {/* Mission Statement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-677">
        <div className="text-center mb-16" data-id="element-678">
          <span className="text-brand-orange font-bold tracking-widest uppercase mb-4 block text-sm" data-id="element-679">
            Our Mission
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-medium text-brand-black mb-8 leading-tight" data-id="element-680">
            "We publish work that challenges, expands, and deepens the
            conversation around fat justice."
          </h2>
        </div>

        <div className="prose prose-lg max-w-none" data-id="element-681">
          <p className="text-gray-700 leading-relaxed mb-6" data-id="element-682">
            The Digital Publication is not a blog. It's not a magazine. It's a{' '}
            <strong className="text-brand-orange" data-id="element-683">
              platform for fat people to talk to each other
            </strong>
            —to share ideas, debate strategy, process grief, celebrate joy, and
            imagine futures.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6" data-id="element-684">
            We are not interested in 101-level explainers for thin audiences. We
            assume our readers already understand that fatphobia is real,
            systemic, and deadly. We're here to go deeper.
          </p>
          <p className="text-gray-700 leading-relaxed" data-id="element-685">
            We publish essays, reporting, poetry, visual art, and hybrid forms.
            We pay our contributors. We take our time. We value craft.
          </p>
        </div>
      </section>

      {/* What We Publish */}
      <section className="py-20 bg-white" data-id="element-686">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-687">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-688">
            What We Publish
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-689">
            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-690">
              <PenTool className="h-10 w-10 text-brand-orange mb-4" data-id="element-691" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-692">
                Essays & Criticism
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-693">
                Long-form essays that analyze culture, politics, and lived
                experience. We love theory, but we also love storytelling.
              </p>
              <p className="text-sm text-gray-600" data-id="element-694">
                Recent: "The Architecture of Exclusion," "Hunger as Resistance,"
                "Beyond Representation"
              </p>
            </div>

            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-695">
              <BookOpen className="h-10 w-10 text-brand-orange mb-4" data-id="element-696" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-697">
                Reporting & Investigations
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-698">
                Investigative pieces that expose systems of oppression and hold
                institutions accountable.
              </p>
              <p className="text-sm text-gray-600" data-id="element-699">
                Recent: "Inside the Bariatric Surgery Industry," "How Schools
                Weaponize BMI," "The Cost of Being Fat"
              </p>
            </div>

            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-700">
              <Users className="h-10 w-10 text-brand-orange mb-4" data-id="element-701" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-702">
                Poetry & Creative Work
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-703">
                Poems, hybrid forms, and experimental writing that explore
                fatness, embodiment, and liberation.
              </p>
              <p className="text-sm text-gray-600" data-id="element-704">
                Recent: "Body as Archive," "Ode to My Belly," "Inheritance: A
                Fat Daughter's Reckoning"
              </p>
            </div>

            <div className="border-2 border-brand-black p-8 bg-brand-orange/5" data-id="element-705">
              <Target className="h-10 w-10 text-brand-orange mb-4" data-id="element-706" />
              <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-707">
                Visual Essays & Art
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-708">
                Photography, illustration, comics, and visual storytelling that
                centers fat bodies and fat joy.
              </p>
              <p className="text-sm text-gray-600" data-id="element-709">
                Recent: "Fat Futures" photo series, "Reclaiming Space"
                illustration project
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Team */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-710">
        <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-711">
          Editorial Team
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-712">
          {editors.map((editor, index) => <div key={index} className="flex gap-6" data-id="element-713">
              <div className="w-24 h-24 rounded-full bg-brand-orange flex-shrink-0 border-2 border-brand-black" data-id="element-714"></div>
              <div data-id="element-715">
                <h3 className="text-xl font-bold text-brand-black mb-1" data-id="element-716">
                  {editor.name}
                </h3>
                <p className="text-brand-orange font-bold uppercase text-sm mb-3" data-id="element-717">
                  {editor.role}
                </p>
                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-718">
                  {editor.bio}
                </p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Editorial Values */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-719">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-720">
          <h2 className="text-3xl font-black uppercase mb-12 text-center" data-id="element-721">
            Our Editorial Values
          </h2>

          <div className="space-y-8" data-id="element-722">
            <div className="border-l-4 border-brand-orange pl-6" data-id="element-723">
              <h3 className="text-xl font-bold mb-2" data-id="element-724">
                We Pay Our Contributors
              </h3>
              <p className="text-gray-300" data-id="element-725">
                We believe in compensating creative and intellectual labor. Our
                rates start at $0.25/word for essays and $100 for poems.
              </p>
            </div>

            <div className="border-l-4 border-brand-orange pl-6" data-id="element-726">
              <h3 className="text-xl font-bold mb-2" data-id="element-727">We Take Our Time</h3>
              <p className="text-gray-300" data-id="element-728">
                We don't chase virality or publish on a rigid schedule. We work
                with writers to develop their pieces over weeks or months.
              </p>
            </div>

            <div className="border-l-4 border-brand-orange pl-6" data-id="element-729">
              <h3 className="text-xl font-bold mb-2" data-id="element-730">We Center Fat Voices</h3>
              <p className="text-gray-300" data-id="element-731">
                All our contributors are fat. We don't publish thin people
                writing about their "body image journey" or their fat friends.
              </p>
            </div>

            <div className="border-l-4 border-brand-orange pl-6" data-id="element-732">
              <h3 className="text-xl font-bold mb-2" data-id="element-733">We Value Craft</h3>
              <p className="text-gray-300" data-id="element-734">
                We care about good writing. We work with contributors on
                structure, language, and form. We believe politics and craft are
                inseparable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 text-center" data-id="element-735">
        <div className="max-w-3xl mx-auto" data-id="element-736">
          <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-737">
            Read The Publication
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-738">
            Visit our dedicated editorial platform to read all our published
            work.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-739">
            <Button color="orange" size="lg" withArrow data-id="element-740">
              Go to Publication
            </Button>
            <Button variant="outline" color="orange" size="lg" data-id="element-741">
              Submit Your Work
            </Button>
          </div>
        </div>
      </section>
    </div>;
}